/**
 * ConnectionsList — accepted connections with presence, mute, and a message
 * entry point (Phase B AC-08.1; presence via Phase C).
 */

import { useState } from 'react';
import { useSocialGraph } from '../../context/SocialGraphContext';
import { usePresence } from '../../context/PresenceContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { ProfileCardTrigger } from '../profile/ProfileCardTrigger';
import { PresenceBadge } from './PresenceBadge';
import type { Connection } from '../../types/social';
import './ConnectionsList.css';

interface Props {
  onMessage?: (connectionId: string, connection: Connection) => void;
}

export function ConnectionsList({ onMessage }: Props) {
  const { connections, setMuted, isLoading } = useSocialGraph();
  const presenceEnabled = useFeatureFlag('social.dm_presence');
  const { presenceOf } = usePresence();
  const [busyId, setBusyId] = useState<string | null>(null);

  const toggleMute = async (c: Connection) => {
    setBusyId(c.id);
    try {
      await setMuted(c.id, !c.muted);
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading && connections.length === 0) {
    return <p className="connections-list__empty">Loading connections…</p>;
  }
  if (connections.length === 0) {
    return <p className="connections-list__empty">No connections yet.</p>;
  }

  return (
    <ul className="connections-list">
      {connections.map((c) => (
        <li className="connections-list__row" key={c.id}>
          <ProfileCardTrigger profileId={c.otherProfileId}>
            <div className="connections-list__who">
              <div
                className="connections-list__avatar"
                style={
                  c.otherAvatarUrl ? { backgroundImage: `url(${c.otherAvatarUrl})` } : undefined
                }
              />
              <div>
                <span className="connections-list__name">{c.otherDisplayName}</span>
                {presenceEnabled && (
                  <PresenceBadge presence={presenceOf(c.otherProfileId) ?? c.presence} showLabel />
                )}
              </div>
            </div>
          </ProfileCardTrigger>

          <div className="connections-list__actions">
            <button
              className="connections-list__btn"
              disabled={busyId === c.id}
              onClick={() => toggleMute(c)}
              title={c.muted ? 'Unmute' : 'Mute'}
            >
              {c.muted ? 'Unmute' : 'Mute'}
            </button>
            <button
              className="connections-list__btn connections-list__btn--primary"
              onClick={() => onMessage?.(c.id, c)}
            >
              Message
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default ConnectionsList;
